import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Card, Button, Space, message, Popconfirm, Row, Col, Input, Select, Drawer, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/Layout';
import jobType from './../../services/jobType';
import './index.css';
import { auditColumns } from '../../utils/audit';

const JobTypePage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);

  // Fetch data using React Query
  const { data, isLoading, error } = useQuery({
    queryKey: ['jobType'],
    queryFn: async () => {
      const response = await jobType.get();
      return response;
    },
  });

  // Add mutation
  const addMutation = useMutation({
    mutationFn: (values) => jobType.post(values),
    onSuccess: () => {
      message.success('Successfully added');
      queryClient.invalidateQueries(['jobType']);
      setIsDrawerVisible(false);
    },
    onError: (error) => {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to save: ' + error.message);
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, values }) => jobType.put(id, values),
    onSuccess: () => {
      message.success('Successfully updated');
      queryClient.invalidateQueries(['jobType']);
      setIsDrawerVisible(false);
    },
    onError: (error) => {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update: ' + error.message);
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => jobType.delete(id),
    onSuccess: () => {
      message.success('Successfully deleted');
      queryClient.invalidateQueries(['jobType']);
    },
    onError: () => {
      message.error('Failed to delete');
    },
  });

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsDrawerVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditingId(record.id);
    setIsDrawerVisible(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate({ id: editingId, values });
      } else {
        addMutation.mutate(values);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filteredValue: [search],
      onFilter: (value, record) => 
        record.name.toLowerCase().includes(value.toLowerCase()),
    },
    ...auditColumns,
    {
      title: 'Actions',
      fixed: 'right',
      width: 120,
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (error) {
    message.error('Failed to fetch data');
  }

  return (
    <PageLayout>
      <Card title="Job Type">
        <Row gutter={[16, 16]} className="table-header">
          <Col flex="1">
            <Input
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add New
            </Button>
          </Col>
        </Row>

        <Table
          size='small'
          columns={columns}
          dataSource={data || []}
          loading={isLoading}
          rowKey="id"
        />

        <Drawer
          title={editingId ? "Edit Job Type" : "Add Job Type"}
          placement="right"
          onClose={() => setIsDrawerVisible(false)}
          open={isDrawerVisible}
          width={400}
          extra={
            <Space>
              <Button onClick={() => setIsDrawerVisible(false)}>Cancel</Button>
              <Button 
                type="primary" 
                onClick={handleSubmit}
                loading={addMutation.isPending || updateMutation.isPending}
              >
                Submit
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the name!' }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Drawer>
      </Card>
    </PageLayout>
  );
};

export default JobTypePage;